import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { Horario, NumeroDetalle, NumerosCatalogos } from "../../../../services/v2/numeros.service";
import GraficaNumero from "./graficaNumero";

const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";
type Pestana = "resumen" | "perfil" | "ajustes" | "automaticos" | "conexion" | "historial";
const PESTANAS: { k: Pestana; t: string }[] = [
	{ k: "resumen", t: "Resumen" }, { k: "perfil", t: "Perfil de WhatsApp" }, { k: "ajustes", t: "Ajustes y horario" },
	{ k: "automaticos", t: "Mensajes automáticos" }, { k: "conexion", t: "Conexión y seguridad" }, { k: "historial", t: "Historial" },
];
export const CALIDAD: Record<string, { t: string; c: string }> = {
	GREEN: { t: "Verde", c: "ok" }, YELLOW: { t: "Amarilla", c: "medio" }, RED: { t: "Roja", c: "mal" }, UNKNOWN: { t: "Sin calificar", c: "nada" },
};
export const LIMITE: Record<string, string> = {
	UNTIERED: "Número nuevo (sin escalón)", TIER_50: "50 clientes nuevos al día", TIER_250: "250 al día", TIER_1K: "1,000 al día", TIER_10K: "10,000 al día", TIER_100K: "100,000 al día", TIER_UNLIMITED: "Sin límite",
};
export const hace = (iso: string | null): string => {
	if (!iso) return "nunca";
	const t = new Date(iso.includes("T") || iso.endsWith("Z") ? iso : iso.replace(" ", "T") + "Z").getTime();
	const m = Math.max(0, Math.round((Date.now() - t) / 60000));
	if (m < 1) return "ahora";
	if (m < 60) return "hace " + m + " min";
	const h = Math.round(m / 60);
	if (h < 48) return "hace " + h + " h";
	return "hace " + Math.round(h / 24) + " días";
};
const fechaHora = (iso: string) => {
	const d = new Date(iso.replace(" ", "T") + (iso.includes("T") ? "" : "Z"));
	return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" }) + ", " + d.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" }).replace(/\s?([ap])\.?\s?m\.?/i, " $1m");
};
const HORARIO_BASE: Horario = { zona: "America/Mexico_City", dias: { 1: [["08:00", "18:00"]], 2: [["08:00", "18:00"]], 3: [["08:00", "18:00"]], 4: [["08:00", "18:00"]], 5: [["08:00", "18:00"]], 6: [["08:00", "14:00"]], 7: [] } as any };

interface Props { campusId: number; cat: NumerosCatalogos; id: number; onClose: () => void; onCambio: () => void; }

/** Un número completo: estado, perfil, ajustes, automáticos, conexión e historial (Esteban, 2026-10-06). */
const NumeroDetallePanel: React.FC<Props> = ({ campusId, cat, id, onClose, onCambio }) => {
	const http = HttpManager.getInstance();
	const [n, setN] = useState<NumeroDetalle | null>(null);
	const [pestana, setPestana] = useState<Pestana>("resumen");
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState<boolean>(false);
	// perfil
	const [perfil, setPerfil] = useState<any>({});
	const [foto, setFoto] = useState<File | null>(null);
	const [fotoUrl, setFotoUrl] = useState<string | null>(null);
	// ajustes
	const [aj, setAj] = useState<any>({});
	// automáticos
	const [auto, setAuto] = useState<any>({});
	const [pin, setPin] = useState<string>("");
	const [codigo, setCodigo] = useState<string>("");

	const cargar = (r: NumeroDetalle) => {
		setN(r);
		const p = r.estado_meta?.perfil || {};
		setPerfil({ about: (p.about || "").trim(), description: p.description || "", address: p.address || "", email: p.email || "",
			websites: [...(p.websites || []), "", ""].slice(0, 2), vertical: p.vertical || "EDU" });
		setAj({ etiqueta: r.etiqueta || "", dept: r.dept, campus_id: r.campus_id, ruta: r.ruta || "", activo: r.activo, horario: r.horario });
		setAuto({ bienvenida: r.bienvenida, fuera_horario: r.fuera_horario, bienvenida_meta: r.automatizacion.bienvenida_meta,
			preguntas: [...r.automatizacion.preguntas, "", "", "", ""].slice(0, 4), comandos: r.automatizacion.comandos });
	};
	useEffect(() => {
		setN(null);
		http.numerosService.detalle(campusId, id).then((r) => cargar(r.data)).catch((e) => setError(errorTexto(e)));
	}, [id]);
	useEffect(() => {
		if (!foto) { setFotoUrl(null); return; }
		const u = URL.createObjectURL(foto);
		setFotoUrl(u);
		return () => URL.revokeObjectURL(u);
	}, [foto]);

	const hacer = async (fn: () => Promise<{ data: NumeroDetalle }>, ok: string) => {
		setOcupado(true); setError(null); setAviso(null);
		try { const r = await fn(); cargar(r.data); setAviso(ok); onCambio(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setOcupado(false); }
	};

	const L = cat.limites_perfil;
	const malos = useMemo(() => (n?.revision || []).filter((r) => !r.ok), [n]);
	if (!n) {
		return <div className="nu-panel"><div className="nu-panel-cab"><button className="nu-x" onClick={onClose} aria-label="Cerrar">×</button></div>{error ? <div className="nu-aviso oro">{error}</div> : <div className="loader" />}</div>;
	}
	const cal = CALIDAD[n.calidad || "UNKNOWN"] || CALIDAD.UNKNOWN;
	const contador = (v: string, max: number) => <small className={v.length > max ? "pasa" : ""}>{v.length}/{max}</small>;
	const h: Horario = aj.horario || HORARIO_BASE;
	const setTramo = (dia: number, i: number, j: 0 | 1, v: string) => {
		const dias = { ...h.dias } as any;
		const t = [...(dias[dia] || [])];
		t[i] = j === 0 ? [v, t[i][1]] : [t[i][0], v];
		dias[dia] = t;
		setAj({ ...aj, horario: { ...h, dias } });
	};

	return (
		<div className="nu-panel" role="dialog" aria-modal="true">
			<div className="nu-panel-cab">
				<div className="nu-avatar grande">{n.foto ? <img src={n.foto} alt="" /> : <span>W</span>}</div>
				<div className="nu-panel-tit">
					<h2>{n.etiqueta || n.telefono}</h2>
					<span>{n.telefono} · {n.nombre || "Sin nombre"} · {n.dept_nombre}{n.plantel ? " · " + n.plantel : n.territorio ? " · " + n.territorio : ""}</span>
				</div>
				<span className={"nu-chip cal-" + cal.c}>Calidad {cal.t.toLowerCase()}</span>
				{n.woztell ? <span className="nu-chip oro">También en Woztell</span> : null}
				{!n.activo ? <span className="nu-chip">Apagado</span> : null}
				<button className="nu-btn" disabled={ocupado} onClick={() => hacer(() => http.numerosService.refrescar(campusId, n.id), "Estado actualizado con Meta.")}>{ocupado ? "…" : "Actualizar"}</button>
				<button className="nu-x" onClick={onClose} aria-label="Cerrar">×</button>
			</div>
			<div className="nu-pestanas" role="tablist">
				{PESTANAS.map((p) => (
					<button key={p.k} role="tab" aria-selected={pestana === p.k} className={pestana === p.k ? "on" : ""} onClick={() => setPestana(p.k)}>
						{p.t}{p.k === "resumen" && malos.length ? <span className="nu-globo">{malos.length}</span> : null}
					</button>
				))}
			</div>
			<div className="nu-panel-cuerpo">
				{error ? <div className="nu-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
				{aviso ? <div className="nu-aviso ok" onClick={() => setAviso(null)}>{aviso}</div> : null}

				{pestana === "resumen" ? (
					<>
						<div className="nu-kpis">
							<div className="nu-kpi"><span>Límite de envío</span><b>{n.limite ? LIMITE[n.limite] || n.limite : "—"}</b></div>
							<div className="nu-kpi"><span>Recibidos 24 h</span><b>{n.in24.toLocaleString("es-MX")}</b><small>último {hace(n.ultimo_in)}</small></div>
							<div className="nu-kpi"><span>Enviados 24 h</span><b>{n.out24.toLocaleString("es-MX")}</b><small>último {hace(n.ultimo_out)}</small></div>
							<div className={"nu-kpi" + (n.fallidos24 ? " oro" : "")}><span>Fallidos 24 h</span><b>{n.fallidos24}</b></div>
							<div className="nu-kpi"><span>Plantillas aprobadas</span><b>{n.plantillas}</b></div>
						</div>
						<div className="nu-bloque">
							<div className="nu-bloque-tit">Revisión</div>
							<ul className="nu-revision">
								{n.revision.map((r) => <li key={r.clave} className={r.ok ? "ok" : "mal"}><span aria-hidden="true">{r.ok ? "✓" : "!"}</span>{r.texto}</li>)}
							</ul>
							{n.estado_at ? <div className="nu-tenue">Revisado con Meta {hace(n.estado_at)}{n.error_meta ? " · Error: " + n.error_meta : ""}</div> : null}
						</div>
						<div className="nu-bloque">
							<div className="nu-bloque-tit">Últimos 30 días</div>
							<GraficaNumero serie={n.serie} />
						</div>
						{n.plantillas_wapp.length ? (
							<div className="nu-bloque">
								<div className="nu-bloque-tit">Plantillas de WAPP en este número</div>
								<div className="nu-lista-simple">{n.plantillas_wapp.map((p) => <span key={p.id}><a href={"/plantillas?id=" + p.id}>{p.nombre}</a> <small>{p.estado === "APPROVED" ? "aprobada" : p.estado.toLowerCase()}</small></span>)}</div>
							</div>
						) : null}
					</>
				) : null}

				{pestana === "perfil" ? (
					<div className="nu-dos">
						<div className="nu-form">
							<div className="nu-bloque">
								<div className="nu-bloque-tit">Foto de perfil</div>
								<label className="nu-subir-foto">
									<div className="nu-avatar enorme">{fotoUrl || n.foto ? <img src={fotoUrl || n.foto || ""} alt="" /> : <span>W</span>}</div>
									<span><b>{foto ? foto.name : "Cambiar foto"}</b><small>JPG o PNG cuadrada, ideal 640×640, hasta 5 MB. Usa el logo oficial.</small></span>
									<input type="file" hidden accept="image/jpeg,image/png" onChange={(e) => setFoto(e.target.files?.[0] || null)} />
								</label>
							</div>
							<label className="nu-campo"><span>Acerca de {contador(perfil.about || "", L.about)}</span>
								<input value={perfil.about || ""} placeholder="Ej. Carreras técnicas en Monterrey 🎓" onChange={(e) => setPerfil({ ...perfil, about: e.target.value })} /></label>
							<label className="nu-campo"><span>Descripción {contador(perfil.description || "", L.descripcion)}</span>
								<textarea rows={4} value={perfil.description || ""} placeholder="Qué ofrece la escuela, horarios de atención, cómo inscribirse…" onChange={(e) => setPerfil({ ...perfil, description: e.target.value })} /></label>
							<label className="nu-campo"><span>Dirección {contador(perfil.address || "", L.direccion)}</span>
								<input value={perfil.address || ""} onChange={(e) => setPerfil({ ...perfil, address: e.target.value })} /></label>
							<div className="nu-fila">
								<label className="nu-campo crece"><span>Correo</span><input type="email" value={perfil.email || ""} onChange={(e) => setPerfil({ ...perfil, email: e.target.value })} /></label>
								<label className="nu-campo"><span>Categoría</span>
									<select value={perfil.vertical || "EDU"} onChange={(e) => setPerfil({ ...perfil, vertical: e.target.value })}>
										{Object.entries(cat.categorias).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
									</select></label>
							</div>
							{[0, 1].map((i) => (
								<label key={i} className="nu-campo"><span>Sitio web {i + 1}</span>
									<input value={perfil.websites?.[i] || ""} placeholder="https://washingtonacademy.com" onChange={(e) => { const w = [...(perfil.websites || ["", ""])]; w[i] = e.target.value; setPerfil({ ...perfil, websites: w }); }} /></label>
							))}
							<div className="nu-acciones">
								<button className="nu-btn pri" disabled={ocupado} onClick={() => hacer(() => http.numerosService.perfil(campusId, n.id, perfil, foto), "Perfil guardado en WhatsApp.").then(() => setFoto(null))}>{ocupado ? "Guardando en WhatsApp…" : "Guardar perfil"}</button>
							</div>
						</div>
						{/* Así se ve al tocar el nombre del negocio en WhatsApp. */}
						<div className="nu-perfil-vista" aria-label="Vista previa del perfil">
							<div className="nu-avatar enorme">{fotoUrl || n.foto ? <img src={fotoUrl || n.foto || ""} alt="" /> : <span>W</span>}</div>
							<b className="nu-pv-nombre">{n.nombre || "Washington Academy"}</b>
							<span className="nu-pv-tel">{n.telefono}</span>
							<span className="nu-pv-tipo">Cuenta de empresa</span>
							{perfil.about ? <div className="nu-pv-fila"><small>Acerca de</small>{perfil.about}</div> : null}
							{perfil.description ? <div className="nu-pv-fila">{perfil.description}</div> : null}
							<div className="nu-pv-fila"><small>Categoría</small>{cat.categorias[perfil.vertical] || "—"}</div>
							{perfil.address ? <div className="nu-pv-fila"><small>Dirección</small>{perfil.address}</div> : null}
							{perfil.email ? <div className="nu-pv-fila"><small>Correo</small>{perfil.email}</div> : null}
							{(perfil.websites || []).filter(Boolean).map((w: string) => <div key={w} className="nu-pv-fila liga">{w}</div>)}
						</div>
					</div>
				) : null}

				{pestana === "ajustes" ? (
					<div className="nu-form">
						<div className="nu-fila">
							<label className="nu-campo crece"><span>Nombre en WAPP</span><input value={aj.etiqueta} placeholder="Ej. HN Ventas SPS" onChange={(e) => setAj({ ...aj, etiqueta: e.target.value })} /></label>
							<label className="nu-campo"><span>Departamento</span>
								<select value={aj.dept} onChange={(e) => setAj({ ...aj, dept: e.target.value })}>{Object.entries(cat.departamentos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
							<label className="nu-campo"><span>Plantel</span>
								<select value={aj.campus_id || ""} onChange={(e) => setAj({ ...aj, campus_id: e.target.value ? Number(e.target.value) : null })}>
									<option value="">Todo el país</option>{cat.planteles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
							<label className="nu-campo"><span>Rueda de asesoras</span>
								<select value={aj.ruta || ""} onChange={(e) => setAj({ ...aj, ruta: e.target.value })}>
									<option value="">Ninguna</option>{cat.rutas.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}</select></label>
						</div>
						<label className="nu-switch"><input type="checkbox" checked={!!aj.activo} onChange={(e) => setAj({ ...aj, activo: e.target.checked })} />
							<span>{aj.activo ? "Activo: se usa en WappChat, Plantillas y para contestar" : "Apagado: no se usa para nada en WAPP"}</span></label>
						<div className="nu-bloque">
							<div className="nu-bloque-tit">
								<span>Horario de atención</span>
								<label className="nu-switch chico"><input type="checkbox" checked={!!aj.horario} onChange={(e) => setAj({ ...aj, horario: e.target.checked ? HORARIO_BASE : null })} /><span>{aj.horario ? "Con horario" : "Sin horario"}</span></label>
							</div>
							{aj.horario ? (
								<>
									<label className="nu-campo"><span>Zona horaria</span>
										<select value={h.zona} onChange={(e) => setAj({ ...aj, horario: { ...h, zona: e.target.value } })}>
											<option value="America/Mexico_City">México y Honduras (centro)</option><option value="America/Monterrey">Monterrey</option><option value="America/Tegucigalpa">Tegucigalpa</option>
										</select></label>
									<div className="nu-horario">
										{Object.entries(cat.dias).map(([num, nombre]) => {
											const tramos: [string, string][] = (h.dias as any)[num] || [];
											return (
												<div key={num} className="nu-dia">
													<b>{nombre}</b>
													{tramos.length === 0 ? <span className="nu-tenue">Cerrado</span> : tramos.map((t, i) => (
														<span key={i} className="nu-tramo">
															<input type="time" value={t[0]} onChange={(e) => setTramo(Number(num), i, 0, e.target.value)} />–
															<input type="time" value={t[1]} onChange={(e) => setTramo(Number(num), i, 1, e.target.value)} />
															<button className="nu-quitar" onClick={() => { const d = { ...h.dias } as any; d[num] = tramos.filter((_, j) => j !== i); setAj({ ...aj, horario: { ...h, dias: d } }); }} aria-label="Quitar">×</button>
														</span>
													))}
													{tramos.length < 3 ? <button className="nu-agregar" onClick={() => { const d = { ...h.dias } as any; d[num] = [...tramos, ["09:00", "13:00"]]; setAj({ ...aj, horario: { ...h, dias: d } }); }}>+ horario</button> : null}
												</div>
											);
										})}
									</div>
								</>
							) : <div className="nu-tenue">Sin horario, WAPP considera que siempre se atiende (no manda "fuera de horario").</div>}
						</div>
						<div className="nu-acciones"><button className="nu-btn pri" disabled={ocupado} onClick={() => hacer(() => http.numerosService.ajustes(campusId, n.id, aj), "Ajustes guardados.")}>Guardar ajustes</button></div>
					</div>
				) : null}

				{pestana === "automaticos" ? (
					<div className="nu-form">
						{n.woztell ? <div className="nu-aviso oro">Este número sigue en Woztell: su bot contesta ahí, así que WAPP no manda bienvenida ni fuera de horario hasta que se migre.</div> : null}
						{(["bienvenida", "fuera_horario"] as const).map((k) => (
							<div key={k} className="nu-bloque">
								<div className="nu-bloque-tit">
									<span>{k === "bienvenida" ? "Bienvenida" : "Fuera de horario"}</span>
									<label className="nu-switch chico"><input type="checkbox" checked={!!auto[k]?.activa} onChange={(e) => setAuto({ ...auto, [k]: { ...auto[k], activa: e.target.checked } })} /><span>{auto[k]?.activa ? "Prendido" : "Apagado"}</span></label>
								</div>
								<div className="nu-tenue">{k === "bienvenida" ? "Le llega a quien le escribe por primera vez a este número." : "Le llega a quien escribe fuera del horario de atención, máximo una vez cada 12 horas."} Se puede usar {"{{nombre}}"} y {"{{saludo}}"}.</div>
								<textarea rows={3} value={auto[k]?.texto || ""} placeholder={k === "bienvenida" ? "¡Hola {{nombre}}! Gracias por escribir a Washington Academy 😊 En un momento te atiende una asesora." : "¡Hola {{nombre}}! Ahorita estamos fuera de horario. Mañana a primera hora te contestamos 😊"}
									onChange={(e) => setAuto({ ...auto, [k]: { ...auto[k], texto: e.target.value } })} />
							</div>
						))}
						<div className="nu-acciones"><button className="nu-btn pri" disabled={ocupado} onClick={() => hacer(() => http.numerosService.ajustes(campusId, n.id, { bienvenida: auto.bienvenida, fuera_horario: auto.fuera_horario }), "Mensajes automáticos guardados.")}>Guardar mensajes</button></div>

						<div className="nu-bloque">
							<div className="nu-bloque-tit">Al abrir el chat (WhatsApp)</div>
							<div className="nu-tenue">WhatsApp le enseña al cliente estas preguntas para tocar, antes de que escriba. Máximo 4, de 80 caracteres.</div>
							{auto.preguntas?.map((p: string, i: number) => (
								<input key={i} className="nu-input" value={p} maxLength={80} placeholder={["¿Qué cursos tienen?", "¿Cuánto cuesta?", "¿Qué horarios hay?", "Quiero inscribirme"][i]}
									onChange={(e) => { const q = [...auto.preguntas]; q[i] = e.target.value; setAuto({ ...auto, preguntas: q }); }} />
							))}
							<label className="nu-switch chico"><input type="checkbox" checked={!!auto.bienvenida_meta} onChange={(e) => setAuto({ ...auto, bienvenida_meta: e.target.checked })} />
								<span>Avisar cuando alguien abre el chat por primera vez (manda la bienvenida aunque no haya escrito)</span></label>
							<div className="nu-bloque-tit chico">Comandos <small>el cliente escribe "/" y los ve</small></div>
							{(auto.comandos || []).map((c: any, i: number) => (
								<div key={i} className="nu-fila">
									<input className="nu-input" style={{ maxWidth: 160 }} value={c.nombre} placeholder="horarios" onChange={(e) => { const x = [...auto.comandos]; x[i] = { ...c, nombre: e.target.value }; setAuto({ ...auto, comandos: x }); }} />
									<input className="nu-input" value={c.descripcion} placeholder="Ver los horarios de este mes" onChange={(e) => { const x = [...auto.comandos]; x[i] = { ...c, descripcion: e.target.value }; setAuto({ ...auto, comandos: x }); }} />
									<button className="nu-quitar" onClick={() => setAuto({ ...auto, comandos: auto.comandos.filter((_: any, j: number) => j !== i) })} aria-label="Quitar">×</button>
								</div>
							))}
							{(auto.comandos || []).length < 30 ? <button className="nu-agregar" onClick={() => setAuto({ ...auto, comandos: [...(auto.comandos || []), { nombre: "", descripcion: "" }] })}>+ Comando</button> : null}
							<div className="nu-acciones"><button className="nu-btn pri" disabled={ocupado} onClick={() => hacer(() => http.numerosService.automatizacion(campusId, n.id, { preguntas: auto.preguntas.filter((x: string) => x.trim()), comandos: auto.comandos, bienvenida_meta: auto.bienvenida_meta }), "Guardado en WhatsApp.")}>Guardar en WhatsApp</button></div>
						</div>
					</div>
				) : null}

				{pestana === "conexion" ? (
					<div className="nu-form">
						<div className="nu-bloque">
							<div className="nu-bloque-tit">Conexión</div>
							<ul className="nu-revision">
								<li className={n.conexion === "CONNECTED" ? "ok" : "mal"}><span>{n.conexion === "CONNECTED" ? "✓" : "!"}</span>WhatsApp: {n.conexion || "sin dato"}</li>
								<li className={n.wapp ? "ok" : "mal"}><span>{n.wapp ? "✓" : "!"}</span>{n.wapp ? "WAPP conectado a la cuenta de Meta de este número" : "WAPP no está conectado a esta cuenta"}</li>
								<li className={n.woztell ? "mal" : "ok"}><span>{n.woztell ? "!" : "✓"}</span>{n.woztell ? "Woztell también está conectado" : "Woztell ya no está conectado"}</li>
							</ul>
							<div className="nu-detalle-meta">
								<span>Cuenta de Meta <b>{n.waba_id}</b></span><span>Id del número <b>{n.phone_number_id}</b></span>
								<span>Plataforma <b>{n.estado_meta?.plataforma || "—"}</b></span><span>Velocidad <b>{n.estado_meta?.velocidad || "—"}</b></span>
								<span>Modo <b>{n.estado_meta?.modo || "—"}</b></span><span>Webhook <b>{n.estado_meta?.webhook ? "WAPP" : "—"}</b></span>
							</div>
							{n.conexion !== "CONNECTED" && n.estado_meta?.en_app ? (
								<div className="nu-pasos">
									<b>Este número vive en la app WhatsApp Business del teléfono</b>
									<div className="nu-tenue">Meta no deja conectarlo con PIN. Cierra esta ficha y usa "Conectar número de la app WhatsApp Business" arriba en Números: inicias sesión con Facebook, eliges este número y confirmas en el teléfono. El teléfono sigue funcionando igual.</div>
								</div>
							) : !n.wapp || n.conexion !== "CONNECTED" ? (
								<div className="nu-fila">
									{n.conexion !== "CONNECTED" ? <input className="nu-input" style={{ maxWidth: 180 }} inputMode="numeric" maxLength={6} value={pin} placeholder="PIN de 6 dígitos" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} /> : null}
									<button className="nu-btn pri" disabled={ocupado} onClick={() => hacer(() => http.numerosService.conectar(campusId, n.id, pin || null), "Conectado a WAPP.")}>Conectar a WAPP</button>
								</div>
							) : null}
							{n.conexion !== "CONNECTED" && !n.estado_meta?.en_app ? (
								<div className="nu-pasos">
									<b>Si Meta pide verificar el número</b>
									<div className="nu-tenue">Meta manda un código de 6 números al teléfono de este número. Escríbelo aquí y luego vuelve a tocar "Conectar a WAPP".</div>
									<div className="nu-fila">
										<button className="nu-btn" disabled={ocupado} onClick={() => hacer(() => http.numerosService.codigo(campusId, n.id, "SMS"), "Código enviado por SMS al " + n.telefono + ".")}>Mandar código por SMS</button>
										<button className="nu-btn" disabled={ocupado} onClick={() => hacer(() => http.numerosService.codigo(campusId, n.id, "VOICE"), "Llamada en camino al " + n.telefono + ".")}>Por llamada</button>
										<input className="nu-input" style={{ maxWidth: 150 }} inputMode="numeric" maxLength={6} value={codigo} placeholder="Código" onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} />
										<button className="nu-btn pri" disabled={ocupado || codigo.length !== 6} onClick={() => hacer(() => http.numerosService.verificar(campusId, n.id, codigo), "Número verificado. Ahora toca \"Conectar a WAPP\".").then(() => setCodigo(""))}>Verificar</button>
									</div>
								</div>
							) : null}
							{n.woztell ? (
								<div className="nu-pasos">
									<b>Para sacarlo de Woztell</b>
									<ol>
										<li>Pídele a Woztell que desconecte este número de su plataforma (que quite su app de la cuenta de Meta {n.waba_id}).</li>
										<li>Cuando lo hagan, toca "Actualizar": aquí debe decir "Woztell ya no está conectado".</li>
										<li>Prende la bienvenida y el fuera de horario en "Mensajes automáticos", porque el bot de Woztell ya no contestará.</li>
									</ol>
								</div>
							) : null}
						</div>
						<div className="nu-bloque">
							<div className="nu-bloque-tit">Verificación en dos pasos</div>
							<div className="nu-tenue">{n.pin ? "Activa. Si alguien intenta registrar este número en otro lado, WhatsApp le pide este PIN." : "Sin PIN: cualquiera con acceso al SMS podría registrar el número en otro lado. Pon uno y guárdalo en un lugar seguro."}</div>
							<div className="nu-fila">
								<input className="nu-input" style={{ maxWidth: 180 }} inputMode="numeric" maxLength={6} type="password" autoComplete="new-password" value={pin} placeholder="Nuevo PIN (6 dígitos)" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} />
								<button className="nu-btn" disabled={ocupado || pin.length !== 6} onClick={() => hacer(() => http.numerosService.pin(campusId, n.id, pin), "PIN guardado.").then(() => setPin(""))}>{n.pin ? "Cambiar PIN" : "Poner PIN"}</button>
							</div>
						</div>
						<div className="nu-bloque">
							<div className="nu-bloque-tit">Nombre que ve el cliente</div>
							<div className="nu-tenue">"{n.nombre || "—"}" · {n.nombre_estado === "APPROVED" ? "aprobado por Meta" : n.nombre_estado === "PENDING_REVIEW" ? "en revisión" : "sin aprobar"}. El nombre se pide o se cambia en el Administrador de WhatsApp de Meta.</div>
							<a className="nu-btn" href={"https://business.facebook.com/wa/manage/phone-numbers/?waba_id=" + n.waba_id} target="_blank" rel="noreferrer">Abrir en Meta</a>
						</div>
					</div>
				) : null}

				{pestana === "historial" ? (
					<div className="nu-bloque">
						<div className="nu-bloque-tit">Cambios con Meta</div>
						{n.historial.length === 0 ? <div className="nu-tenue">Sin cambios registrados todavía.</div> : (
							<ul className="nu-historial">{n.historial.map((x) => <li key={x.id}><small>{fechaHora(x.at)}</small><span>{x.cambio}</span></li>)}</ul>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default NumeroDetallePanel;
